'use client';

import { useEffect, useRef, useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { openDeferredEventSource } from '@/lib/open-deferred-event-source';
import { nextReconnectDelay } from '@/lib/sse-reconnect-delay';
import type { ChatMessageData } from '@/components/chat/chat-message';

export interface TypingUser {
  address: string;
  display_name: string;
  expiresAt: number;
}

interface UseChatStreamOptions {
  enabled: boolean;
  getAccessToken: () => Promise<string | null>;
  ownAddress?: string | null;
  /** Chat room to subscribe to. Accepts any server-validated `ChatRoom`
   *  id — the reserved labels `'boardroom'` / `'megapot-grid'` or a live
   *  composite grid id (`0x<ugm>-<tokenId>`). The SSE endpoint rejects
   *  anything else, so we forward the caller-supplied value verbatim.
   *  Defaults to `'boardroom'` so legacy consumers that don't pass a
   *  room still see the main room. */
  room?: string;
}

export function useChatStream({ enabled, getAccessToken, ownAddress, room = 'boardroom' }: UseChatStreamOptions) {
  const queryClient = useQueryClient();
  const [isConnected, setIsConnected] = useState(false);
  const [typingUsers, setTypingUsers] = useState<TypingUser[]>([]);
  const closeEsRef = useRef<(() => void) | null>(null);
  const reconnectRef = useRef<NodeJS.Timeout | null>(null);
  const typingTimerRef = useRef<NodeJS.Timeout | null>(null);
  // Bounded exponential backoff. Bumped on every `onerror`, reset
  // to 0 on every successful `connected` event. Without this the
  // hook hammered a 429'd `/api/chat/stream` on a flat 5s timer
  // and never recovered until the user reloaded.
  const reconnectAttemptRef = useRef(0);

  // Prune expired typing users every second
  useEffect(() => {
    if (!enabled) return;
    typingTimerRef.current = setInterval(() => {
      setTypingUsers((prev) => {
        const now = Date.now();
        const next = prev.filter((u) => u.expiresAt > now);
        return next.length === prev.length ? prev : next;
      });
    }, 1_000);
    return () => { clearInterval(typingTimerRef.current!); };
  }, [enabled]);

  const connect = useCallback(async () => {
    if (!enabled) return;
    closeEsRef.current?.();

    const token = await getAccessToken();
    if (!token) return;

    const url = `/api/chat/stream?token=${encodeURIComponent(token)}&room=${encodeURIComponent(room)}`;
    closeEsRef.current = openDeferredEventSource(url, (es) => {
      es.addEventListener('connected', () => {
        setIsConnected(true);
        reconnectAttemptRef.current = 0;
      });

      es.addEventListener('message', (e) => {
      try {
        const msg = JSON.parse(e.data) as ChatMessageData;
        queryClient.setQueryData<{ pages: ChatMessageData[][]; pageParams: unknown[] }>(
          ['chat-messages', room],
          (old) => {
            if (!old) return old;
            const first = old.pages[0] || [];
            if (first.some((m) => m.id === msg.id)) return old;
            return { ...old, pages: [[msg, ...first], ...old.pages.slice(1)] };
          }
        );
      } catch { /* ignore malformed */ }
    });

    es.addEventListener('edit', (e) => {
      try {
        const { id, message, edited_at } = JSON.parse(e.data) as {
          id: number; message: string; edited_at: number;
        };
        queryClient.setQueryData<{ pages: ChatMessageData[][]; pageParams: unknown[] }>(
          ['chat-messages', room],
          (old) => {
            if (!old) return old;
            return {
              ...old,
              pages: old.pages.map((page) =>
                page.map((m) => (m.id === id ? { ...m, message, edited_at } : m))
              ),
            };
          }
        );
      } catch { /* ignore */ }
    });

    es.addEventListener('delete', (e) => {
      try {
        const { id } = JSON.parse(e.data) as { id: number };
        queryClient.setQueryData<{ pages: ChatMessageData[][]; pageParams: unknown[] }>(
          ['chat-messages', room],
          (old) => {
            if (!old) return old;
            return {
              ...old,
              pages: old.pages.map((page) => page.filter((m) => m.id !== id)),
            };
          }
        );
      } catch { /* ignore */ }
    });

    es.addEventListener('reaction', (e) => {
      try {
        const { message_id, emoji, count, address: reactAddr, added } = JSON.parse(e.data) as {
          message_id: number; emoji: string; count: number; address: string; added: boolean;
        };
        queryClient.setQueryData<{ pages: ChatMessageData[][]; pageParams: unknown[] }>(
          ['chat-messages', room],
          (old) => {
            if (!old) return old;
            return {
              ...old,
              pages: old.pages.map((page) =>
                page.map((m) => {
                  if (m.id !== message_id) return m;
                  const reactions = [...(m.reactions ?? [])];
                  const idx = reactions.findIndex((r) => r.emoji === emoji);
                  if (count === 0) {
                    if (idx >= 0) reactions.splice(idx, 1);
                  } else if (idx >= 0) {
                    const isOwn = ownAddress ? reactAddr.toLowerCase() === ownAddress.toLowerCase() : false;
                    reactions[idx] = {
                      ...reactions[idx],
                      count,
                      reacted: isOwn ? added : reactions[idx].reacted,
                    };
                  } else {
                    const isOwn = ownAddress ? reactAddr.toLowerCase() === ownAddress.toLowerCase() : false;
                    reactions.push({ emoji, count, reacted: isOwn && added });
                  }
                  return { ...m, reactions };
                })
              ),
            };
          }
        );
      } catch { /* ignore */ }
    });

    es.addEventListener('typing', (e) => {
      try {
        const { address, display_name } = JSON.parse(e.data) as {
          address: string; display_name: string;
        };
        if (ownAddress && address.toLowerCase() === ownAddress.toLowerCase()) return;
        setTypingUsers((prev) => {
          const expiresAt = Date.now() + 4_000;
          const exists = prev.findIndex((u) => u.address === address);
          if (exists >= 0) {
            const next = [...prev];
            next[exists] = { address, display_name, expiresAt };
            return next;
          }
          return [...prev, { address, display_name, expiresAt }];
        });
      } catch { /* ignore */ }
    });

      es.onerror = () => {
        setIsConnected(false);
        closeEsRef.current?.();
        closeEsRef.current = null;
        const delay = nextReconnectDelay(reconnectAttemptRef.current);
        reconnectAttemptRef.current += 1;
        reconnectRef.current = setTimeout(() => connect(), delay);
      };
    });
  }, [enabled, getAccessToken, queryClient, ownAddress, room]);

  useEffect(() => {
    if (enabled) {
      connect();
    }
    return () => {
      closeEsRef.current?.();
      closeEsRef.current = null;
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      setIsConnected(false);
      setTypingUsers([]);
    };
  }, [enabled, connect]);

  return { isConnected, typingUsers };
}
